import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button, Card, Form, FormProps, Typography } from 'antd';
import OTP from 'components/otp/otp';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import { useNavigate } from 'react-router-dom';
import { changePhone, getIsRedirectedFromContactPage, getOtpTTL, resendOtp, verifyPhone } from '../services/contactProfileService';
import NotificationService from 'core/services/notification';
import { checkPhoneValidation } from 'utils/validators/phone';
import authService from 'core/services/auth';
import { useDispatch, useSelector } from 'react-redux';
import { setAdmin } from 'stores/useAdminStore';
import { RootState } from 'stores/store';
import Recaptcha from 'components/recaptcha/recaptcha';
import styles from '../pages/register.module.scss';
import { formatErrorMessages } from 'utils/errorMessage';
import ReCAPTCHA from 'react-google-recaptcha';

const RegisterOtp: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const otpRecaptchaRef = useRef<ReCAPTCHA>(null);
	const changePhoneRecaptchaRef = useRef<ReCAPTCHA>(null);

	const [changeNumberForm] = Form.useForm();

	type FieldType = {
		phone_number: string;
		phone_country_code: string;
		recaptcha_token: string;
	};

	const admin = useSelector((state: RootState) => state.admin);

	const [currentState, setCurrentState] = useState<'otp' | 'phone'>('otp');
	const [isInvalidOtp, setIsInvalidOtp] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isResendLoading, setIsResendLoading] = useState(false);
	const [changeNumberLoading, setChangeNumberLoading] = useState(false);
	const [isRedirectedFromContactPage, setIsRedirectedFromContactPage] = useState(getIsRedirectedFromContactPage());

	const onSubmitOtp = async (otp: string) => {
		try {
			setIsLoading(true);

			await verifyPhone({ code: otp.toString() });
			otpRecaptchaRef.current?.reset();

			navigate('/register/company');
		} catch (error: any) {
			setIsLoading(false);
			otpRecaptchaRef.current?.reset();

			if (error?.status === 422)
				Object.entries(error?.response?.data?.data?.errors).forEach(([key, value]) => {
					if (key === 'otp') setIsInvalidOtp(true);
				});
			else NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const onVerifyRecaptcha = async (token: string | null) => {
		if (token) {
			setIsRedirectedFromContactPage(true);
			onResendOtp(token);
		} else {
			setIsRedirectedFromContactPage(false);
		}
	};

	const onResendOtp = async (token: string) => {
		try {
			setIsResendLoading(true);
			await resendOtp(token);
			NotificationService.success(t('COMMON.SUCCESS'), t('REGISTER.OTP_RESENT'));
			setIsResendLoading(false);
		} catch (error) {
			setIsResendLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const validatePhone = (_: any, value: any) => {
		const number = changeNumberForm.getFieldValue('phone_number');
		if (checkPhoneValidation(changeNumberForm.getFieldValue('phone_country_code'), number)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		changeNumberForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const onSubmitPhoneNumber: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setChangeNumberLoading(true);

			const data = await changePhone(values);

			authService.setData(true, [], 'phone_verified');
			dispatch(setAdmin(data.data.company_user));
			changePhoneRecaptchaRef.current?.reset();
			changeNumberForm.setFieldValue('recaptcha_token', null);

			NotificationService.success(t('COMMON.SUCCESS'), t('REGISTER.PHONE_CHANGED'));

			setCurrentState('otp');
			setChangeNumberLoading(false);
		} catch (error: any) {
			setChangeNumberLoading(false);
			changePhoneRecaptchaRef.current?.reset();
			changeNumberForm.setFieldValue('recaptcha_token', null);

			NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
		}
	};

	return (
		<>
			{/* OTP Div */}
			{currentState === 'otp' && (
				<div className="position-relative">
					{isRedirectedFromContactPage && (
						<>
							<motion.h2
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{
									duration: 0.5,
									delay: 0.3,
								}}
								key="title">
								{t('REGISTER.VERIFY_PHONE')}
							</motion.h2>

							<motion.p
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{
									duration: 0.5,
									delay: 0.5,
								}}
								key="subTitle"
								className="text-light">
								{t('REGISTER.ENTER_OTP')}
								&nbsp;
								<Typography.Text style={{ fontSize: 16, color: '#1677ff', direction: 'ltr', display: 'inline-block' }}>
									{`+${admin?.phone_country_code}${admin?.phone_number}`}
								</Typography.Text>
							</motion.p>

							<motion.p
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{
									duration: 0.5,
									delay: 0.5,
								}}
								key="subTitle2"
								className="text-light">
								{t('REGISTER.WRONG_NUMBER')}
								<Button type="link" className="py-0" style={{ paddingInlineStart: '10px' }} onClick={() => setCurrentState('phone')}>
									{t('COMMON.CHANGE')}
								</Button>
							</motion.p>
						</>
					)}

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.5,
							delay: 0.5,
						}}
						key="otpInput"
						style={{ maxWidth: 'var(--inputWidth)', margin: 'auto' }}>
						<Card>
							{!isRedirectedFromContactPage && (
								<>
									<motion.h5
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{
											duration: 0.5,
											delay: 0.3,
										}}
										key="title">
										{t('REGISTER.VERIFY_NOT_REBOT')}
									</motion.h5>
								</>
							)}

							<Recaptcha
								centered
								style={{ marginBottom: 30, display: isRedirectedFromContactPage ? 'none' : 'block' }}
								onSubmit={onVerifyRecaptcha}
								onExpired={() => {}}
								ref={otpRecaptchaRef}
							/>

							{isRedirectedFromContactPage && (
								<OTP
									ttl={getOtpTTL()}
									onConfirm={onSubmitOtp}
									onResend={onResendOtp}
									isLoading={isLoading}
									isResendLoading={isResendLoading}
									isInvalid={isInvalidOtp}
									onChange={() => setIsInvalidOtp(false)}
								/>
							)}
						</Card>
					</motion.div>
				</div>
			)}

			{/* Change Phone Number Div */}
			{currentState === 'phone' && (
				<div>
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.5,
							delay: 0.3,
						}}
						key="phone_title">
						{t('REGISTER.CHANGE_PHONE_NUMBER')}
					</motion.h2>

					<motion.p
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.5,
							delay: 0.5,
						}}
						key="phone_subTitle"
						className="text-light">
						{t('REGISTER.CHANGE_DESC')}
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.5,
							delay: 0.5,
						}}
						key="phone_input"
						style={{ maxWidth: 'var(--inputWidth)', margin: 'auto' }}>
						<Card style={{ paddingBlock: 20 }}>
							<Form name="registerForm" onFinish={onSubmitPhoneNumber} form={changeNumberForm} layout="vertical" autoComplete="off">
								<Form.Item
									name="phone_number"
									rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
									<PhoneInputComponent noAutoComplete onChange={onChangePhone} label={t('REGISTER.REPRESENTATIVE_PHONE_NUMBER')} />
								</Form.Item>
								<Form.Item name="phone_country_code" hidden noStyle />

								<Form.Item name="recaptcha_token" rules={[{ required: true, message: t('COMMON.RECAPTCHA_REQUIRED') }]}>
									<Recaptcha
										centered
										onSubmit={(token) => {
											changeNumberForm.setFieldValue('recaptcha_token', token);
										}}
										ref={changePhoneRecaptchaRef}
									/>
								</Form.Item>

								<Button block type="primary" htmlType="submit" className="mt-4" loading={changeNumberLoading}>
									{t('COMMON.CHANGE')}
								</Button>
							</Form>
						</Card>
					</motion.div>
				</div>
			)}
		</>
	);
};

export default RegisterOtp;
